import React, { useMemo, useState } from "react";
import { TwelveColumn } from "@onnit-js/ui/components/module/grid/12/Grid";
import Box, { BoxProps } from "@onnit-js/ui/components/box/Box";
import { Text } from "@onnit-js/ui/components/text";
import { FaSortDown } from "react-icons/fa";
import { Img } from "@onnit-js/ui/components/image";
import SelectField from "@onnit-js/ui/components/form/selectfield/SelectField";
import { TrialProduct } from "@onnit-js/ui/components/trials/disclaimers/trials";
import { Trial, trials } from "../trials";
import TrialButton from "./TrialButton";
import TrialBuyBox from "./TrialBuyBox";
import usePageData from "../../../hooks/usePageData";

interface Props extends BoxProps {
    pageImgUrl: string;
    activeTrial: Trial;
}

const TrialInfo: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = (
    {
        pageImgUrl,
        activeTrial,
        ...rest
    }) => {
    const { img, imgAltText, author, quote } = activeTrial;

    return (
        <Box {...rest}>
            <Box textAlign="center" mb={[4, 4, 6, 6]} marginTop="-20px">
                <FaSortDown fontSize="42px" />
            </Box>
            <TwelveColumn alignItems="center">
                <Box gridColumn={["1 / -1", "1 / -1", "1 / span 6", "1 / span 6"]}>
                    <Box width={["60%", "initial"]}>
                        <Img src={`${pageImgUrl}/${img}`} alt={imgAltText} />
                    </Box>
                </Box>
                <Box gridColumn={["1 / -1", "1 / -1", "7 / span 6", "7 / span 6"]} pt={[4, 0]} pl={[0, 6]}>
                    <Text
                        typeStyle="text01"
                        fontWeight="heavy"
                        fontStyle="italic"
                        dangerouslySetInnerHTML={{ __html: `"${quote}"` }}
                    />
                    <Text mt={4} typeStyle="text03" fontWeight="bold" color="grays.4">
                        &mdash; {author}
                    </Text>
                </Box>
            </TwelveColumn>
        </Box>
    );
};

interface TrialsProps {
    imgUrl: string;
    imgParams: string;
    pageImgUrl: string;
}

const Trials: React.FC<React.PropsWithChildren<React.PropsWithChildren<TrialsProps>>> = (
    {
        imgUrl,
        imgParams,
        pageImgUrl,
        ...rest
    }) => {
    const defaultSelectedButtonId = TrialProduct.TotalGutHealth;
    const [selectedButtonId, setSelectedButtonId] = useState<TrialProduct>(defaultSelectedButtonId);

    const { findProduct } = usePageData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const buttonsVisible: TrialProduct[] = [
        TrialProduct.TotalGutHealth,
        TrialProduct.TotalHuman,
        TrialProduct.AlphaBrain,
        TrialProduct.NewMood,
        TrialProduct.ShroomTechSport,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const filterFullProductsVisible = (buttonsVisible: TrialProduct[]) => buttonsVisible.map((trialProductId) => (
        findProduct(trials[trialProductId].productId)
    )).filter((product) => product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const filterTrialProductsVisible = (buttonsVisible: TrialProduct[]) => buttonsVisible.map((trialProductId) => (
        findProduct(trials[trialProductId].trialProductId)
    )).filter((product) => product);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fullProducts = useMemo(() => filterFullProductsVisible(buttonsVisible), [buttonsVisible]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const trialProducts = useMemo(() => filterTrialProductsVisible(buttonsVisible), [buttonsVisible]);

    const ariaControlsId = "trialInfo";

    const handleTrialSelectChange = (id: string) => {
        const selectedTrialProductId = buttonsVisible.filter((trialProductId) => trialProductId === id).shift();
        if (selectedTrialProductId) {
            setSelectedButtonId(selectedTrialProductId);
        }
    };

    const handleTrialButtonClick = (id: TrialProduct) => {
        setSelectedButtonId(id);
    };

    return (
        <>
            <Box textAlign="center">
                <Text mt={6} mb={4} typeStyle="text04" fontWeight="bold" textTransform="uppercase" color="#45788a">
                    Free TRIAL. Just Pay Shipping
                </Text>
                <Text as="h1" fontStyle="italic" typeStyle="title02" fontWeight="black" textTransform="uppercase">
                    Try Onnit's Top Products for Free
                </Text>
                <Box mt={4} mb={[4, 6, 6, 8]}>
                    <Text typeStyle="text02" fontWeight="bold">
                        Just pay shipping, and we’ll send you a free supply.
                    </Text>
                </Box>
            </Box>
            <TwelveColumn>
                <Box
                    {...rest}
                    gridColumn={["1 / -1", "1 / -1", "2 / span 10", "3 / span 8"]}
                    justifyContent="center"
                >
                    <Box
                        display={["none", "none", "none", "grid"]}
                        gridGap={2}
                        gridTemplateColumns={[
                            "repeat(auto-fit, minmax(100px, 1fr))",
                            "repeat(auto-fit, minmax(100px, 1fr))",
                            "repeat(auto-fit, minmax(215px, 1fr))",
                            "repeat(auto-fit, minmax(160px, max-content))",
                            "repeat(auto-fit, minmax(192px, 1fr))"
                        ]}
                        alignItems="center"
                        justifyContent="center"
                    >
                        {buttonsVisible.map((button) => (
                            <TrialButton
                                imgUrl={imgUrl}
                                isOpen={selectedButtonId === button}
                                onClick={() => handleTrialButtonClick(button)}
                                trial={trials[button]}
                                key={button}
                                ariaControlsId={ariaControlsId}
                            />
                        ))}
                    </Box>

                    <Box display={["block", "block", "block", "none"]}>
                        <SelectField
                            label="Select a Trial Product"
                            value={selectedButtonId}
                            aria-controls={ariaControlsId}
                            onChange={(event) => (handleTrialSelectChange(event.target.value))}
                        >
                            {buttonsVisible.map((button) => (
                                // eslint-disable-next-line jsx-a11y/control-has-associated-label
                                <option
                                    value={button}
                                    key={button}
                                    /* eslint-disable-next-line react/no-danger */
                                    dangerouslySetInnerHTML={{ __html: trials[button].btnTitle }}
                                />
                            ))}
                        </SelectField>
                    </Box>

                    <TrialInfo pageImgUrl={pageImgUrl} activeTrial={trials[selectedButtonId]} mt={4} />
                </Box>
            </TwelveColumn>
            <Box role="region" id={ariaControlsId} aria-live="polite" aria-atomic="true" aria-relevant="additions text">
                <TrialBuyBox
                    fullProducts={fullProducts}
                    trialProducts={trialProducts}
                    activeTrial={trials[selectedButtonId]}
                    activeTrialProduct={selectedButtonId}
                    imgUrl={imgUrl}
                    imgParams={imgParams}
                    mt={[6, 4, 8, 8]}
                    px={[5, 0]}
                />
            </Box>
        </>
    );
};

export default Trials;
